import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';

import {
  IMoveSectionGraphqlRequest,
  IPublicationViewMessages,
  IStoriesFolder,
  IStoryListMessages
} from '../../../../types';
import { useUpdatePublicationUpdatedAt } from '../../Publications/PublicationView/hooks/usePublicationMutations';
import { countAncestors, disableInvalidNodes, getDescendentTreeHeight, getParentNodeForNode } from '../../../utils';
import { useMoveFolders } from '../hooks';

import { styles } from './MoveFolderModal.styles';

export interface IMoveFolderModalProps extends RouteComponentProps {
  messages: IStoryListMessages;
  folderToMove: IStoriesFolder;
  folderStructure: IStoriesFolder[];
  rootFolderId: string;
  entityId: number;
  publicationViewMessages?: IPublicationViewMessages;
  isFromPublication?: boolean;
  publicationId?: string;
  userId?: number;
  moveSections?(props: IMoveSectionGraphqlRequest): Promise<void>;
  onClose(): void;
}

export const IN_FOLDER = 'IN_FOLDER';
export const NO_FOLDER = 'NO_FOLDER';

export const MoveFolderModal: React.FC<IMoveFolderModalProps> = (props) => {
  const {
    folderToMove,
    folderStructure,
    entityId,
    messages,
    onClose,
    rootFolderId,
    publicationViewMessages,
    isFromPublication = false,
    publicationId,
    moveSections,
    userId
  } = props;

  const currentParentFolder = getParentNodeForNode(folderToMove, folderStructure) as IStoriesFolder;
  const updatePublicationUpdatedAtMutation = useUpdatePublicationUpdatedAt();
  const moveFoldersMutation = useMoveFolders({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderMoveFailed
  });
  const [accordionOptions, setAccordionOptions] = useState([]);
  const [targetFolder, setTargetFolder] = useState(null);
  const [location, setLocation] = useState(!currentParentFolder ? NO_FOLDER : IN_FOLDER);

  const targetFolderDepth = countAncestors(folderStructure, targetFolder);
  const currentFolderDepth = getDescendentTreeHeight(folderStructure, folderToMove.id);

  const nestedTooDeep = targetFolderDepth + currentFolderDepth > 5;

  const getDisabledNodeTree = (folderTree) => {
    if (!folderTree || !folderToMove) return [];
    const isNodeValid = (folder) => folder.id === folderToMove.id;
    return disableInvalidNodes(
      folderTree,
      isNodeValid,
      isFromPublication ? publicationViewMessages.cantMoveToChild : messages.cantMoveToChild
    );
  };

  useEffect(() => {
    setAccordionOptions(getDisabledNodeTree(folderStructure));
  }, [folderStructure]);

  const accordionListValue = () => {
    if (targetFolder) {
      return targetFolder;
    } else if (currentParentFolder) {
      return currentParentFolder.id || currentParentFolder;
    } else {
      return null;
    }
  };

  const folderHasMoved = () => {
    if (location === NO_FOLDER) {
      return currentParentFolder !== null;
    } else {
      const currentParentId = currentParentFolder && currentParentFolder.id;
      const accListSelection = accordionListValue();
      return accListSelection && currentParentId !== accListSelection;
    }
  };

  const onMoveFolder = () => {
    const parentFolderId = location === NO_FOLDER ? rootFolderId : targetFolder;
    if (isFromPublication) {
      void moveSections({
        ids: [folderToMove.id],
        parentFolderId: parentFolderId,
        entityId: entityId,
        currentParentFolder: !!currentParentFolder,
        publicationId,
        isMovedToRootFolder: parentFolderId === rootFolderId
      }).then(() => {
        updatePublicationUpdatedAtMutation.mutate({ publicationId, userId });
      });
    } else {
      moveFoldersMutation.mutate({
        ids: [folderToMove.id],
        parentFolderId: parentFolderId,
        entityId: entityId
      });
    }
    onClose();
  };

  return (
    <div data-test="move-folder-modal">
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="move-folder-dialog-title">
        <DialogTitle id="move-folder-dialog-title" sx={styles.dialogTitle}>
          {isFromPublication ? publicationViewMessages.moveSection : messages.moveFolder}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Box sx={styles.modalDescription}>
            <Typography variant="body2">
              {messages.moving} <strong>{folderToMove && folderToMove.displayName}</strong>{' '}
              {currentParentFolder && (
                <>
                  {messages.from} <strong>{currentParentFolder.displayName}</strong>
                </>
              )}
            </Typography>
          </Box>

          <FormControl sx={styles.locationFormControl} component="fieldset">
            <FormLabel component="legend" sx={styles.formLabel}>{messages.location}</FormLabel>
            <RadioGroup
              sx={styles.radioGroup}
              value={location}
              onChange={(_e, value) => setLocation(value)}
              data-test="location-radio-group"
            >
              <FormControlLabel
                value={NO_FOLDER}
                sx={styles.radioButton}
                control={
                  <Radio
                    inputProps={{ 'data-test': 'location-option-no-folder', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={
                  <Box>
                    {isFromPublication ? publicationViewMessages.noParentSection : messages.noParent}
                    {location === NO_FOLDER && (
                      <Box sx={styles.radioDescription}>
                        <Typography variant="body2" color="text.secondary">
                          {isFromPublication ? publicationViewMessages.moveStoriesToRoot : messages.moveStoriesToRoot}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                value={IN_FOLDER}
                sx={styles.radioButton}
                control={
                  <Radio
                    inputProps={{ 'data-test': 'location-option-in-folder', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={
                  <Box>
                    {isFromPublication ? publicationViewMessages.inSection : messages.inFolder}
                    {location === IN_FOLDER && (
                      <Box sx={styles.radioDescription}>
                        <FolderTreeSelect
                          activeNodeId={accordionListValue()}
                          data={accordionOptions}
                          dataTest="folder-accordion-select"
                          disabled={!folderStructure}
                          maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                          onNodeSelect={(node) => setTargetFolder(node.id)}
                          showFilter={true}
                          filterPlaceholder={
                            isFromPublication ? publicationViewMessages.filterSections : messages.filterFolders
                          }
                        />
                      </Box>
                    )}
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {location === IN_FOLDER && nestedTooDeep && (
            <FormHelperText error sx={styles.errorMsg}>
              {isFromPublication ? publicationViewMessages.nestedTooDeep : messages.nestedTooDeep}
            </FormHelperText>
          )}
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={onClose}
            data-test="cancel-move-folder-button"
          >
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onMoveFolder}
            data-test="move-folder-button"
            disabled={!folderHasMoved() || (location === IN_FOLDER && nestedTooDeep)}
          >
            {messages.move}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
